import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useEffect, useRef, useState } from 'react';
import { Playlist } from '@/store/playlist/types';
import { usePlaylistStore } from '@/store/playlist/store';
import { useToast } from '@/hooks/use-toast';

interface AutosaveOptions {
  debounceMs?: number;
  playlistId: string;
}

export function usePlaylistAutosave(
  data: Partial<Playlist>, 
  options: AutosaveOptions
) {
  useLanguage();
  const { debounceMs = 1500, playlistId } = options;
  const { toast } = useToast();
  const { updatePlaylist } = usePlaylistStore();
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const prevDataRef = useRef<string>(JSON.stringify(data));
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip first render (initial load)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevDataRef.current = JSON.stringify(data);
      return;
    }

    const currentData = JSON.stringify(data);
    
    // Only autosave if data actually changed
    if (currentData === prevDataRef.current) {
      return;
    }

    prevDataRef.current = currentData;
    setHasUnsavedChanges(true);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for debounced save
    timeoutRef.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        // Verificar tamanho dos dados ANTES de salvar
        const dataSize = new Blob([JSON.stringify(data)]).size;
        const maxSizeBytes = 2 * 1024 * 1024; // 2MB limite seguro para localStorage
        
        if (dataSize > maxSizeBytes) {
          const sizeMB = (dataSize / 1024 / 1024).toFixed(2);
          console.warn(`⚠️ Dados muito grandes: ${sizeMB}MB`);
          
          toast({
            title: translateInterfaceText("⚠️ Aviso: Dados grandes"),
            description: translateInterfaceText("Playlist com {value0}MB. Considere usar imagens menores para evitar problemas.", { value0: (sizeMB) }),
            variant: 'destructive',
            duration: 4000,
          });
        }
        
        // Tentar salvar no store (persiste no localStorage)
        updatePlaylist(playlistId, data);
        
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
        
        toast({
          title: translateInterfaceText("Alterações salvas"),
          description: translateInterfaceText("As mudanças já estão visíveis na URL pública."),
          duration: 2000,
        });
      } catch (error: any) {
        console.error('❌ Erro ao salvar playlist:', error);
        
        // Detectar erro de quota específico
        const isQuotaError = 
          error?.name === 'QuotaExceededError' ||
          error?.code === 22 ||
          error?.message?.includes('quota') ||
          error?.message?.includes('storage');
        
        toast({
          title: translateInterfaceText("Erro ao salvar"),
          description: isQuotaError
            ? translateInterfaceText("Armazenamento cheio. Reduza o tamanho das imagens (logo, favicon, thumbnails) ou remova playlists antigas.")
            : translateInterfaceText("Não foi possível salvar as alterações. Tente novamente."),
          variant: 'destructive',
          duration: 5000,
        });
      } finally {
        setIsSaving(false);
      }
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, debounceMs, playlistId, updatePlaylist, toast]);

  return {
    isSaving,
    lastSaved,
    hasUnsavedChanges,
  };
}
